import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Slideout,
  Tabs,
  TextField,
  Typography,
  Button,
  Icon,
  Alert,
} from '@ann/web-components';
import {
  SlideoutContainer,
  SlideoutHeader,
  SlideoutSubHeader,
  BackButtonWrapper,
  SlideoutContent,
  SlideoutContentWrapper,
  SlideoutActions,
} from './SlideoutDrives.styled';
import { DriveList, DocumentList } from './components';
import AddConfirmationModal from './components/AddConfirmationModal';
import { useSlideoutDrives } from './hooks';

const SlideoutDrives = ({ open, onClose, onAssignSuccess }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [alert, setAlert] = useState({ show: false, severity: 'success', message: '' });
  // Preservar selecciones cuando se abre el modal
  const [preservedSelections, setPreservedSelections] = useState(null);

  const {
    // Drives
    drives,
    drivesLoading,
    fetchDrives,

    // Documentos
    driveDocuments,
    documentsLoading,
    fetchDriveDocuments,

    // Navegación
    isDocumentsView,
    handleBackToList,
    selectedDriveTitle,

    // Selecciones
    selectedDriveIds,
    selectedDocumentIds,
    toggleDriveSelection,
    toggleDocumentSelection,
    selectedCount,
    selectedItems,
    clearSelections,

    // Búsqueda
    searchQuery,
    setSearchQuery,

    // Asignación
    grantAccess,
    isAssigning,

    // Reset
    resetState,
  } = useSlideoutDrives();

  // ✅ OPTIMIZADO: Fetch drives solo cuando se abre (sin dependencia de fetchDrives)
  useEffect(() => {
    if (open) {
      // Solo hacer fetch si realmente es necesario (el hook maneja el caché internamente)
      fetchDrives();
    }
    // ✅ NO incluir fetchDrives en dependencias para evitar re-ejecuciones innecesarias
    // El hook ya maneja el caché internamente
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Handle close
  const handleClose = useCallback(() => {
    resetState();
    onClose?.();
  }, [onClose, resetState]);

  // Handle search
  const handleSearchChange = useCallback((value) => {
    const searchValue = typeof value === 'string' ? value : (value?.target?.value || '');
    setSearchQuery(searchValue);
  }, [setSearchQuery]);

  // Handle drive click (navegar a documentos)
  const handleDriveClick = useCallback((drive) => {
    fetchDriveDocuments(drive);
  }, [fetchDriveDocuments]);

  // Handle Grant Access button - Preservar selecciones y cerrar slideout
  const handleGrantAccessClick = useCallback(() => {
    // Guardar las selecciones actuales antes de cerrar
    const currentSelections = {
      selectedDriveIds: Array.from(selectedDriveIds),
      selectedDocumentIds: Array.from(selectedDocumentIds),
      selectedItems: selectedItems,
    };
    
    // Guardar selecciones preservadas
    setPreservedSelections(currentSelections);
    
    // Cerrar el slideout
    onClose?.();
    
    // Abrir el modal
    setShowConfirmModal(true);
  }, [selectedDriveIds, selectedDocumentIds, selectedItems, onClose]);

  // Handle confirm - Usar selecciones preservadas
  const handleConfirm = useCallback(async () => {
    if (!preservedSelections) return;
    
    // Convertir arrays de vuelta a Sets
    const driveIdsSet = new Set(preservedSelections.selectedDriveIds);
    const docIdsSet = new Set(preservedSelections.selectedDocumentIds);
    
    // Llamar grantAccess con las selecciones preservadas
    const result = await grantAccess(driveIdsSet, docIdsSet);
    
    setShowConfirmModal(false);
    setPreservedSelections(null);
    
    // Limpiar selecciones solo después de confirmar exitosamente
    clearSelections();
    
    if (result.success) {
      // Cambiar a tab 0 (Drives) para ver el feedback
      if (onAssignSuccess) {
        onAssignSuccess();
      }
      
      setAlert({
        show: true,
        severity: 'success',
        message: `Successfully granted access to ${result.successCount} items`,
      });
    } else {
      setAlert({
        show: true,
        severity: 'error',
        message: `Some items failed: ${result.errors.join(', ')}`,
      });
    }

    // Auto-hide alert
    setTimeout(() => {
      setAlert((prev) => ({ ...prev, show: false }));
    }, 4000);
  }, [preservedSelections, grantAccess, clearSelections, onAssignSuccess]);

  // Handle modal close - NO limpiar selecciones, solo cerrar modal
  const handleModalClose = useCallback(() => {
    setShowConfirmModal(false);
    // Las selecciones se mantendrán en el hook cuando se vuelva a abrir el slideout
    setPreservedSelections(null);
  }, []);

  return (
    <>
      <Slideout onClose={handleClose} size="medium" open={open}>
        {isDocumentsView && (
          <Slideout.HeaderActions>
            <BackButtonWrapper onClick={handleBackToList}>
              <Icon name="arrowLeft" size="large" />
            </BackButtonWrapper>
          </Slideout.HeaderActions>
        )}
        <SlideoutContainer>
          <SlideoutHeader>
            <SlideoutSubHeader>
              <Typography category="title" size="large" weight="bold">
                {selectedDriveTitle}
              </Typography>
            </SlideoutSubHeader>
            <TextField
              onChange={handleSearchChange}
              size="medium"
              iconLeft="search"
              placeholder={isDocumentsView ? 'Search files' : 'Search drives'}
              value={searchQuery}
            />
          </SlideoutHeader>

          <SlideoutContent>
            <Tabs defaultIndex={0} size="medium">
              <Tabs.List>
                <Tabs.Tab index={0} size="small">My Drive</Tabs.Tab>
                <Tabs.Tab index={1} size="small" disabled>Shared With Me</Tabs.Tab>
                <Tabs.Tab index={2} size="small" disabled>Company Drive</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel index={0}>
                <SlideoutContentWrapper>
                  {isDocumentsView ? (
                    <DocumentList
                      documents={driveDocuments}
                      loading={documentsLoading}
                      selectedDocumentIds={selectedDocumentIds}
                      onDocumentSelect={toggleDocumentSelection}
                    />
                  ) : (
                    <DriveList
                      drives={drives}
                      loading={drivesLoading}
                      selectedDriveIds={selectedDriveIds}
                      onDriveClick={handleDriveClick}
                      onDriveSelect={toggleDriveSelection}
                    />
                  )}
                </SlideoutContentWrapper>
              </Tabs.Panel>
              <Tabs.Panel index={1}>
                <Typography category="body" size="medium" color="body">
                  Coming soon...
                </Typography>
              </Tabs.Panel>
              <Tabs.Panel index={2}>
                <Typography category="body" size="medium" color="body">
                  Coming soon...
                </Typography>
              </Tabs.Panel>
            </Tabs>
          </SlideoutContent>

          <SlideoutActions>
            <Button
              label={`Grant Access (${selectedCount})`}
              size="medium"
              type="primary"
              onClick={handleGrantAccessClick}
              disabled={selectedCount === 0 || isAssigning}
            />
          </SlideoutActions>
        </SlideoutContainer>
      </Slideout>

      <AddConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleModalClose}
        onConfirm={handleConfirm}
        selectedItems={preservedSelections?.selectedItems || selectedItems}
        isLoading={isAssigning}
      />

      {alert.show && (
        <Alert
          description={alert.message}
          severity={alert.severity}
          maxWidth="500px"
          closable
          autoClose
          autoCloseDelay={4000}
          onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
        />
      )}
    </>
  );
};

SlideoutDrives.displayName = 'SlideoutDrives';

export default memo(SlideoutDrives);

